import { useMemo, useState } from 'react';
import { Plus, Search, X } from 'lucide-react';
import type { Member, MemberPublic } from '@/lib/db/types';
import { Avatar } from '@/components/shared/Avatar';
import { Sheet } from '@/components/shared/Sheet';

const SLOT_SIZE = 64;
const MAX_SLOTS = 4;

export interface GuestSlotPlayer {
  name: string;
  handicap?: string;
}

export type FilledSlot =
  | { kind: 'organizer' }
  | { kind: 'member'; memberId: string }
  | { kind: 'guest'; guestIndex: number; guest: GuestSlotPlayer };

interface Props {
  organizer: Member;
  members: MemberPublic[];
  following: string[];
  selectedMemberIds: string[];
  guests: GuestSlotPlayer[];
  organizerPlaying?: boolean;
  showGuestHandicap?: boolean;
  atCap: boolean;
  onCapHit: () => void;
  onSelectMember: (memberId: string) => void;
  onRemoveMember: (memberId: string) => void;
  onAddGuest: (guest: GuestSlotPlayer) => void;
  onUpdateGuest: (index: number, guest: GuestSlotPlayer) => void;
  onRemoveGuest: (index: number) => void;
}

function firstName(full: string): string {
  return full.trim().split(/\s+/)[0] || full;
}

function handicapLabel(hcp: number | null | undefined, guestHcp?: string): string {
  if (hcp != null && Number.isFinite(hcp)) return `${hcp.toFixed(1)}`;
  const g = guestHcp?.trim();
  if (g) {
    const n = parseFloat(g);
    if (Number.isFinite(n)) return `${n.toFixed(1)}`;
  }
  return '—';
}

function isFilledSlot(slot: FilledSlot | 'empty' | null): slot is FilledSlot {
  return slot !== null && slot !== 'empty';
}

function sortMembers(list: MemberPublic[], following: string[], organizerId: string) {
  return [...list]
    .filter((m) => m.id !== organizerId)
    .sort((a, b) => {
      const aFav = following.includes(a.id) ? 0 : 1;
      const bFav = following.includes(b.id) ? 0 : 1;
      if (aFav !== bFav) return aFav - bFav;
      return a.name.localeCompare(b.name);
    });
}

export function PlayerSlotPicker({
  organizer,
  members,
  following,
  selectedMemberIds,
  guests,
  organizerPlaying = true,
  showGuestHandicap = false,
  atCap,
  onCapHit,
  onSelectMember,
  onRemoveMember,
  onAddGuest,
  onUpdateGuest,
  onRemoveGuest,
}: Props) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [activeSlot, setActiveSlot] = useState<FilledSlot | 'empty' | null>(null);
  const [search, setSearch] = useState('');
  const [guestDraft, setGuestDraft] = useState({ name: '', handicap: '' });
  const [pickerTab, setPickerTab] = useState<'members' | 'guest'>('members');

  const memberById = useMemo(
    () => Object.fromEntries(members.map((m) => [m.id, m])),
    [members],
  );

  const filledSlots = useMemo((): FilledSlot[] => {
    const list: FilledSlot[] = [];
    if (organizerPlaying) list.push({ kind: 'organizer' });
    for (const id of selectedMemberIds) {
      if (memberById[id]) list.push({ kind: 'member', memberId: id });
    }
    guests.forEach((guest, guestIndex) => {
      list.push({ kind: 'guest', guestIndex, guest });
    });
    return list.slice(0, MAX_SLOTS);
  }, [organizerPlaying, selectedMemberIds, guests, memberById]);

  const slots: (FilledSlot | 'empty')[] = [
    ...filledSlots,
    ...Array.from({ length: Math.max(0, MAX_SLOTS - filledSlots.length) }, () => 'empty' as const),
  ];

  const takenMemberIds = useMemo(() => {
    const ids = new Set(selectedMemberIds);
    if (organizerPlaying) ids.add(organizer.id);
    return ids;
  }, [selectedMemberIds, organizerPlaying, organizer.id]);

  const filteredMembers = useMemo(() => {
    const q = search.trim().toLowerCase();
    const base = sortMembers(members, following, organizer.id).filter((m) => !takenMemberIds.has(m.id));
    if (!q) return base;
    return base.filter(
      (m) => m.name.toLowerCase().includes(q) || m.handle.toLowerCase().includes(q),
    );
  }, [members, following, organizer.id, takenMemberIds, search]);

  const openAddPicker = () => {
    if (atCap) {
      onCapHit();
      return;
    }
    setActiveSlot('empty');
    setPickerTab('members');
    setSearch('');
    setGuestDraft({ name: '', handicap: '' });
    setPickerOpen(true);
  };

  const openEditPicker = (slot: FilledSlot) => {
    if (slot.kind === 'organizer') return;
    setActiveSlot(slot);
    setPickerTab(slot.kind === 'guest' ? 'guest' : 'members');
    if (slot.kind === 'guest') {
      setGuestDraft({ name: slot.guest.name, handicap: slot.guest.handicap ?? '' });
    } else {
      setSearch('');
      setGuestDraft({ name: '', handicap: '' });
    }
    setPickerOpen(true);
  };

  const closePicker = () => {
    setPickerOpen(false);
    setActiveSlot(null);
  };

  const pickMember = (memberId: string) => {
    if (isFilledSlot(activeSlot)) {
      if (activeSlot.kind === 'member') {
        onRemoveMember(activeSlot.memberId);
      } else if (activeSlot.kind === 'guest') {
        onRemoveGuest(activeSlot.guestIndex);
      }
    }
    if (atCap && activeSlot === 'empty') {
      onCapHit();
      return;
    }
    onSelectMember(memberId);
    closePicker();
  };

  const submitGuest = () => {
    const name = guestDraft.name.trim();
    if (!name) return;
    if (isFilledSlot(activeSlot)) {
      if (activeSlot.kind === 'member') {
        onRemoveMember(activeSlot.memberId);
      } else if (activeSlot.kind === 'guest') {
        onUpdateGuest(activeSlot.guestIndex, { name, handicap: guestDraft.handicap });
        closePicker();
        return;
      }
    }
    if (atCap && activeSlot === 'empty') {
      onCapHit();
      return;
    }
    onAddGuest({ name, handicap: guestDraft.handicap });
    closePicker();
  };

  const removeActive = () => {
    if (!isFilledSlot(activeSlot) || activeSlot.kind === 'organizer') return;
    if (activeSlot.kind === 'member') onRemoveMember(activeSlot.memberId);
    if (activeSlot.kind === 'guest') onRemoveGuest(activeSlot.guestIndex);
    closePicker();
  };

  const renderSlot = (slot: FilledSlot | 'empty', index: number) => {
    if (slot === 'empty') {
      return (
        <button
          key={`empty-${index}`}
          type="button"
          onClick={openAddPicker}
          className="bt-flat flex min-w-0 flex-1 flex-col items-center gap-1.5 px-0.5"
        >
          <div
            className="flex items-center justify-center rounded-full border-2 border-dashed border-mute/80 bg-cream/50 text-mute transition-colors hover:border-gold hover:text-gold-deep"
            style={{ width: SLOT_SIZE, height: SLOT_SIZE }}
          >
            <Plus size={22} strokeWidth={2.5} />
          </div>
          <span className="max-w-[4.5rem] text-center text-[10px] font-semibold leading-tight text-mute">
            Add player
          </span>
        </button>
      );
    }

    if (slot.kind === 'organizer') {
      return (
        <div key="organizer" className="flex min-w-0 flex-1 flex-col items-center gap-1.5 px-0.5">
          <div className="rounded-full ring-2 ring-gold ring-offset-2 ring-offset-cream">
            <Avatar member={organizer} size={SLOT_SIZE} />
          </div>
          <span className="max-w-[4.5rem] truncate text-center text-[11px] font-bold text-ink">
            {firstName(organizer.name)}
          </span>
          <span className="text-[10px] font-medium text-mute">
            {handicapLabel(organizer.handicap)}
          </span>
        </div>
      );
    }

    if (slot.kind === 'member') {
      const m = memberById[slot.memberId];
      if (!m) return null;
      return (
        <button
          key={`member-${slot.memberId}`}
          type="button"
          onClick={() => openEditPicker(slot)}
          className="bt-flat flex min-w-0 flex-1 flex-col items-center gap-1.5 px-0.5"
        >
          <Avatar member={m} size={SLOT_SIZE} />
          <span className="max-w-[4.5rem] truncate text-center text-[11px] font-bold text-ink">
            {firstName(m.name)}
          </span>
          <span className="text-[10px] font-medium text-mute">{handicapLabel(m.handicap)}</span>
        </button>
      );
    }

    const { guest, guestIndex } = slot;
    return (
      <button
        key={`guest-${guestIndex}`}
        type="button"
        onClick={() => openEditPicker(slot)}
        className="bt-flat flex min-w-0 flex-1 flex-col items-center gap-1.5 px-0.5"
      >
        <Avatar member={{ name: guest.name }} size={SLOT_SIZE} />
        <span className="max-w-[4.5rem] truncate text-center text-[11px] font-bold text-ink">
          {firstName(guest.name)}
        </span>
        <span className="text-[10px] font-medium text-mute">
          {handicapLabel(null, guest.handicap)}
        </span>
      </button>
    );
  };

  const pickerTitle = activeSlot === 'empty'
    ? 'Add player'
    : isFilledSlot(activeSlot) && activeSlot.kind === 'guest'
      ? 'Edit guest'
      : isFilledSlot(activeSlot) && activeSlot.kind === 'member'
        ? 'Change player'
        : 'Add player';

  return (
    <>
      <div className="flex justify-between gap-1">{slots.map((slot, i) => renderSlot(slot, i))}</div>

      {pickerOpen && (
        <Sheet title={pickerTitle} onClose={closePicker}>
          <div className="space-y-4 px-4 pb-8 pt-2">
            {isFilledSlot(activeSlot) && activeSlot.kind !== 'organizer' && (
              <button
                type="button"
                onClick={removeActive}
                className="w-full rounded-xl border border-bad/30 bg-bad/10 py-2.5 text-sm font-bold text-bad"
              >
                Remove player
              </button>
            )}

            <div className="flex gap-1 rounded-xl border border-line bg-cream p-1">
              {(['members', 'guest'] as const).map((tab) => (
                <button
                  key={tab}
                  type="button"
                  onClick={() => setPickerTab(tab)}
                  className={`flex-1 rounded-lg py-2 text-xs font-bold ${
                    pickerTab === tab ? 'bg-navy text-cream' : 'text-ink-soft'
                  }`}
                >
                  {tab === 'members' ? 'Club member' : 'Guest'}
                </button>
              ))}
            </div>

            {pickerTab === 'members' && (
              <>
                <div className="flex items-center gap-2 rounded-[10px] border border-line bg-paper px-3 py-2.5">
                  <Search size={15} className="shrink-0 text-mute" />
                  <input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search members…"
                    className="min-w-0 flex-1 border-none bg-transparent text-sm text-ink outline-none"
                  />
                  {search && (
                    <button type="button" onClick={() => setSearch('')} className="bt-flat flex text-mute">
                      <X size={14} />
                    </button>
                  )}
                </div>
                <div className="max-h-64 overflow-y-auto rounded-[10px] border border-line bg-cream/40">
                  {filteredMembers.length === 0 ? (
                    <div className="px-3 py-8 text-center text-sm text-mute">
                      {search.trim() ? 'No members match' : 'No members available'}
                    </div>
                  ) : (
                    filteredMembers.map((m) => {
                      const isFav = following.includes(m.id);
                      return (
                        <button
                          key={m.id}
                          type="button"
                          onClick={() => pickMember(m.id)}
                          className="flex w-full items-center gap-3 border-b border-line px-3 py-2.5 text-left last:border-b-0 hover:bg-paper"
                        >
                          <Avatar member={m} size={36} />
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-1.5 text-sm font-semibold text-ink">
                              <span className="truncate">{m.name}</span>
                              {isFav && <span className="text-[11px] text-gold">★</span>}
                            </div>
                            <div className="text-[11px] text-mute">
                              {handicapLabel(m.handicap)} hdcp
                            </div>
                          </div>
                        </button>
                      );
                    })
                  )}
                </div>
              </>
            )}

            {pickerTab === 'guest' && (
              <div className="space-y-2 rounded-[10px] bg-cream p-3">
                <p className="text-[11px] leading-snug text-ink-soft">
                  Guests are added to your scorecard only — they don&apos;t need the app.
                </p>
                <input
                  placeholder="Guest name"
                  value={guestDraft.name}
                  onChange={(e) => setGuestDraft((d) => ({ ...d, name: e.target.value }))}
                  className="w-full rounded-lg border border-line bg-paper px-2.5 py-2 text-[13px] outline-none focus:border-gold"
                />
                {showGuestHandicap && (
                  <input
                    placeholder="Handicap (optional)"
                    inputMode="decimal"
                    value={guestDraft.handicap}
                    onChange={(e) => setGuestDraft((d) => ({ ...d, handicap: e.target.value }))}
                    className="w-full rounded-lg border border-line bg-paper px-2.5 py-2 text-[13px] outline-none focus:border-gold"
                  />
                )}
                <button
                  type="button"
                  onClick={submitGuest}
                  disabled={!guestDraft.name.trim()}
                  className="w-full rounded-lg bg-navy py-2.5 text-[13px] font-bold text-cream disabled:opacity-50"
                >
                  {isFilledSlot(activeSlot) && activeSlot.kind === 'guest' ? 'Save guest' : 'Add guest'}
                </button>
              </div>
            )}
          </div>
        </Sheet>
      )}
    </>
  );
}
